import React, { useState, useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
// Removed unused import: import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Element as ElementType, MachineType, MachineState } from '../types';

import { Machine, ElementsPanel, FavoritesPanel, ThemeToggle } from '../components';
// DragTest component removed
import CustomDragLayer from '../components/CustomDragLayer';
import { combineElements, getRandomWord } from '../lib/api';
import { useToast } from '@/hooks/use-toast';
import { Confetti, ConfettiRef } from '../components/ui/confetti';
import { v4 as uuidv4 } from 'uuid';
import { isMobile } from 'react-device-detect';
import { AuthButton } from '@/components/auth/AuthButton';
import GoalPanel from '../components/GoalPanel';
import SkipButton from '../components/SkipButton';
import InteractionModeToggle from '../components/InteractionModeToggle';
import { InteractionModeProvider } from '../contexts/InteractionModeContext';
import { useGameState } from '@/contexts/GameStateContext';

const initialElements: ElementType[] = [
  { id: 'water', name: 'Water', type: 'water' },
  { id: 'fire', name: 'Fire', type: 'fire' },
  { id: 'dirt', name: 'Dirt', type: 'dirt' },
  { id: 'air', name: 'Air', type: 'air' },
];

const initialMachineState: MachineState = {
  input1: null,
  input2: null,
  output: null,
  isProcessing: false,
};

const Index: React.FC = () => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const confettiRef = useRef<ConfettiRef>(null);
  const { isLoading: gameStateLoading, incrementStat } = useGameState();
  const [allElements, setAllElements] = useState<ElementType[]>([...initialElements]);
  const [favorites, setFavorites] = useState<ElementType[]>([]);
  const [itemMachineState, setItemMachineState] = useState<MachineState>({
    ...initialMachineState,
  });
  const [locationMachineState, setLocationMachineState] = useState<MachineState>({
    ...initialMachineState,
  });
  const [characterMachineState, setCharacterMachineState] = useState<MachineState>({
    ...initialMachineState,
  });
  // Removed unused state: const [newDiscoveryCount, setNewDiscoveryCount] = useState<number>(0);
  const [newDiscoveries, setNewDiscoveries] = useState<Record<string, boolean>>({});
  const [elementHighlightCounts, setElementHighlightCounts] = useState<Record<string, number>>({});
  const [droppedGoalElement, setDroppedGoalElement] = useState<ElementType | null>(null);
  const [skipMode, setSkipMode] = useState<boolean>(false);
  const [points, setPoints] = useState<number>(0);
  const [skipCount, setSkipCount] = useState<number>(1); // Start with 1 free skip

  // No need to initialize OpenAI anymore - using Cloud Functions
  useEffect(() => {
    // This useEffect is kept for future initialization needs
  }, [currentUser]);

  const handleDropToMachine = (
    machineType: MachineType,
    slot: 'input1' | 'input2',
    element: ElementType
  ) => {
    const setMachineState = getMachineStateSetter(machineType);

    // No need to clear selected element anymore

    setMachineState((prev) => ({
      ...prev,
      [slot]: element,
    }));
  };

  const handleCombineElements = async (machineType: MachineType) => {
    const machineState = getMachineState(machineType);
    const setMachineState = getMachineStateSetter(machineType);

    if (!machineState.input1 || !machineState.input2) return;

    try {
      setMachineState((prev) => ({
        ...prev,
        isProcessing: true,
        output: null,
      }));

      const result = await combineElements(
        machineType,
        machineState.input1.name,
        machineState.input2.name
      );

      const existingElement = allElements.find((e) => e.name === result.name);
      let newElement: ElementType;

      if (existingElement) {
        newElement = existingElement;
      } else {
        newElement = {
          id: uuidv4(),
          name: result.name,
          type: 'discovery',
          color: result.color,
        };

        setAllElements((prev) => [...prev, newElement]);

        // Removed: setNewDiscoveryCount((prev) => prev + 1);
        // Track this as a new discovery
        setNewDiscoveries((prev) => ({
          ...prev,
          [newElement.id]: true,
        }));

        toast({
          title: 'New Discovery!',
          description: `Created: ${result.name}`,
        });

        // Trigger confetti animation for new discovery
        const duration = 2 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

        const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

        const interval = setInterval(() => {
          const timeLeft = animationEnd - Date.now();

          if (timeLeft <= 0) {
            return clearInterval(interval);
          }

          const particleCount = 50 * (timeLeft / duration);

          // Trigger confetti from random positions
          confettiRef.current?.fire({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          });

          confettiRef.current?.fire({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          });
        }, 250);
      }

      // Only mark as new if it's a truly new discovery (not an existing element)
      const isNewDiscovery = !existingElement;

      setMachineState({
        input1: null,
        input2: null,
        output: newElement,
        isProcessing: false,
      });

      // If it's not a new discovery but was previously marked as new, remove that marking
      if (!isNewDiscovery && newDiscoveries[newElement.id]) {
        setNewDiscoveries((prev) => {
          const updated = { ...prev };
          delete updated[newElement.id];
          return updated;
        });
      }

      setElementHighlightCounts((prev) => {
        const newCounts = { ...prev };
        Object.keys(newCounts).forEach((id) => {
          if (newCounts[id] > 0) {
            newCounts[id] -= 1;
          }
        });
        return newCounts;
      });
    } catch (error) {
      toast({
        title: 'Combination failed',
        description: 'There was an error combining these elements.',
        variant: 'destructive',
      });
      setMachineState((prev) => ({ ...prev, isProcessing: false }));
    }
  };

  const handleClearMachine = (machineType: MachineType) => {
    const machineState = getMachineState(machineType);
    const setMachineState = getMachineStateSetter(machineType);

    // If there's an output, clear its 'new' status
    if (machineState.output) {
      setNewDiscoveries((prev) => {
        const updated = { ...prev };
        delete updated[machineState.output!.id];
        return updated;
      });
    }

    setMachineState({
      input1: null,
      input2: null,
      output: null,
      isProcessing: false,
    });
  };

  const handleOutputDragEnd = (machineType: MachineType) => {
    const machineState = getMachineState(machineType);
    const setMachineState = getMachineStateSetter(machineType);

    if (machineState.output) {
      // Clear the 'new' status for this element
      setNewDiscoveries((prev) => {
        const updated = { ...prev };
        delete updated[machineState.output!.id];
        return updated;
      });

      // Set the dropped element for the GoalPanel
      handleDropToGoal(machineState.output);

      // Clear the output slot
      setMachineState((prev) => ({
        ...prev,
        output: null,
      }));
    }
  };

  const handleAddToFavorites = (element: ElementType) => {
    if (!favorites.some((e) => e.id === element.id)) {
      // Add to favorites
      setFavorites((prev) => [...prev, element]);

      // Remove from Elements & Discoveries
      setAllElements((prev) => prev.filter((e) => e.id !== element.id));
    }
  };

  const handleRemoveFromFavorites = (elementId: string) => {
    // Find the element before removing it from favorites
    const elementToRemove = favorites.find((e) => e.id === elementId);

    // Remove from favorites
    setFavorites((prev) => prev.filter((e) => e.id !== elementId));

    // Add back to Elements & Discoveries if it exists
    if (elementToRemove) {
      // Check if it's a basic element (water, fire, dirt, air)
      const isBasicElement = ['water', 'fire', 'dirt', 'air'].includes(elementToRemove.id);

      // Only add back if it's not already in allElements
      const alreadyExists = allElements.some((e) => e.id === elementId);

      if (!alreadyExists) {
        // If it's a basic element, we need to make sure it's in the right position
        if (isBasicElement) {
          // Get the basic elements in the right order
          const basicElements = ['water', 'fire', 'dirt', 'air'];
          const basicElementIndex = basicElements.indexOf(elementToRemove.id);

          // Create a new array with the basic element in the right position
          const newElements = [...allElements];

          // Insert at the correct position
          newElements.splice(basicElementIndex, 0, elementToRemove);

          setAllElements(newElements);
        } else {
          // For non-basic elements, just add to the end
          setAllElements((prev) => [...prev, elementToRemove]);
        }
      }
    }
  };

  // No click-to-click functionality

  const handleDropToGoal = (element: ElementType) => {
    // Check if the element is in allElements or favorites
    const isInAllElements = allElements.some((e) => e.id === element.id);
    const isInFavorites = favorites.some((e) => e.id === element.id);

    // Only set the dropped element if it's in one of the collections
    if (isInAllElements || isInFavorites) {
      // First clear any existing dropped element to ensure state is reset
      setDroppedGoalElement(null);

      // Use setTimeout to ensure the state update happens in the next tick
      setTimeout(() => {
        setDroppedGoalElement(element);
      }, 0);
    }
  };

  // This function will be called when an axe is dropped on the GoalPanel
  const handleSkipActivated = () => {
    if (skipCount > 0) {
      setSkipMode(true);
      setSkipCount((prev) => prev - 1);

      // Reset the skip mode after 10 seconds if not used
      setTimeout(() => {
        setSkipMode(false);
      }, 10000);

      // Show a toast to guide the user
      toast({
        title: 'Skip Ready',
        description: 'Click on a goal to skip it and get a new word',
        variant: 'warning',
      });
    }
  };

  const handleGoalSubmit = () => {
    setDroppedGoalElement(null);
  };

  const handlePointsEarned = (earnedPoints: number) => {
    setPoints((prev) => prev + earnedPoints);

    // Award a skip when points are earned (match is found)
    setSkipCount((prev) => prev + 1);
    // Increment the skipsEarned stat
    incrementStat('skipsEarned', 1);

    // Show confetti for points earned
    const duration = 1.5 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 20, spread: 360, ticks: 40, zIndex: 0 };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 30 * (timeLeft / duration);

      // Trigger confetti from random positions
      confettiRef.current?.fire({
        ...defaults,
        particleCount,
        origin: { x: 0.5, y: 0.7 },
      });
    }, 250);
  };

  const handleSkipUsed = async (
    goalType: 'Item' | 'Location' | 'Character',
    currentWord?: string
  ) => {
    // Reset the skip mode
    setSkipMode(false);

    // Increment the skipsUsed stat
    incrementStat('skipsUsed');

    try {
      // Get a new random word for the skipped goal, passing the current word to ensure we get a different one
      const newWord = await getRandomWord(goalType, currentWord);
      return newWord;
    } catch (error) {
      console.error(`Error getting new ${goalType} after skipping:`, error);
      // Return default values if API fails
      return goalType === 'Item' ? 'Shield' : goalType === 'Location' ? 'Paris' : 'Cleopatra';
    }
  };

  const getMachineState = (machineType: MachineType): MachineState => {
    switch (machineType) {
      case 'Item':
        return itemMachineState;
      case 'Location':
        return locationMachineState;
      case 'Character':
        return characterMachineState;
      default:
        return itemMachineState;
    }
  };

  const getMachineStateSetter = (machineType: MachineType) => {
    switch (machineType) {
      case 'Item':
        return setItemMachineState;
      case 'Location':
        return setLocationMachineState;
      case 'Character':
        return setCharacterMachineState;
      default:
        return setItemMachineState;
    }
  };

  // Use TouchBackend for mobile devices, HTML5Backend for desktop
  const dndBackend = isMobile ? TouchBackend : HTML5Backend;

  // If game state is loading, show a loading screen
  if (gameStateLoading && currentUser) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-background z-50">
        <div className="flex items-center justify-center gap-4 mb-4">
          <img
            src="/images/elements.png"
            alt="Elements"
            className="w-16 h-16 object-contain drop-shadow-[2px_2px_3px_rgba(0,0,0,0.3)] animate-pulse"
          />
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
            VibeCraft
          </h1>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-lg text-muted-foreground">Loading your elements...</p>
        </div>
      </div>
    );
  }

  return (
    <InteractionModeProvider>
      <DndProvider backend={dndBackend}>
        <CustomDragLayer />
        <Confetti
          ref={confettiRef}
          className="fixed left-0 top-0 z-50 w-full h-full pointer-events-none"
        />
        <div className="container mx-auto px-4 py-8 overflow-visible">
          <header className="text-center mb-8 relative flex flex-col items-center">
            <div className="absolute right-0 top-0 flex items-center gap-2">
              <InteractionModeToggle />
              <AuthButton />
              <ThemeToggle />
            </div>
            <div className="flex items-center justify-center gap-4">
              <img
                src="/images/elements.png"
                alt="Elements"
                className="w-12 h-12 object-contain drop-shadow-[2px_2px_3px_rgba(0,0,0,0.3)]"
              />
              <h1 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
                VibeCraft
              </h1>
            </div>
            <p className="mt-2 text-muted-foreground">Combine elements to discover new ones</p>
          </header>

          <div className="grid grid-cols-12 gap-6 overflow-visible">
            <div className="col-span-10">
              <div className="mb-8">
                <ElementsPanel
                  elements={allElements}
                  highlightCounts={elementHighlightCounts}
                  onAddToFavorites={handleAddToFavorites}
                  onElementDragEnd={handleDropToGoal}
                  title="Elements & Discoveries"
                  description="Drag elements to machines to combine them"
                  favorites={favorites}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 overflow-visible">
                <Machine
                  type="Item"
                  state={itemMachineState}
                  onDrop={(slot, element) => handleDropToMachine('Item', slot, element)}
                  onCombine={() => handleCombineElements('Item')}
                  onClear={() => handleClearMachine('Item')}
                  onOutputDragEnd={() => handleOutputDragEnd('Item')}
                  newDiscoveries={newDiscoveries}
                />
                <Machine
                  type="Location"
                  state={locationMachineState}
                  onDrop={(slot, element) => handleDropToMachine('Location', slot, element)}
                  onCombine={() => handleCombineElements('Location')}
                  onClear={() => handleClearMachine('Location')}
                  onOutputDragEnd={() => handleOutputDragEnd('Location')}
                  newDiscoveries={newDiscoveries}
                />
                <Machine
                  type="Character"
                  state={characterMachineState}
                  onDrop={(slot, element) => handleDropToMachine('Character', slot, element)}
                  onCombine={() => handleCombineElements('Character')}
                  onClear={() => handleClearMachine('Character')}
                  onOutputDragEnd={() => handleOutputDragEnd('Character')}
                  newDiscoveries={newDiscoveries}
                />
              </div>

              <div className="grid grid-cols-10 gap-4">
                <div className="col-span-8">
                  <GoalPanel
                    droppedElement={droppedGoalElement}
                    skipMode={skipMode}
                    onSubmit={handleGoalSubmit}
                    onPointsEarned={handlePointsEarned}
                    onSkipUsed={handleSkipUsed}
                  />
                </div>
                <div className="col-span-2 bg-card rounded-xl shadow-lg p-4 border border-border flex flex-col items-center justify-between">
                  <div className="flex flex-col items-center gap-2">
                    <h3 className="text-lg font-bold">Score</h3>
                    <div className="text-3xl font-bold text-primary">{points}</div>
                    <div className="mt-2">
                      <SkipButton count={skipCount} onSkip={handleSkipActivated} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-2 hidden md:block">
              <FavoritesPanel
                favorites={favorites}
                onRemove={handleRemoveFromFavorites}
                onAddToFavorites={handleAddToFavorites}
              />
            </div>
          </div>
        </div>
      </DndProvider>
    </InteractionModeProvider>
  );
};

export default Index;
